import { API_BASE_URL } from "../../../config";
import { isMoneySubmitted, moneySubmittedState } from "./moneySubmitted";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const SaleReportManagement = () => {
    const today = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

    const [saleDataToday, setSaleDataToday] = useState([]); // State for today's sale data
    const [saleDataAll, setSaleDataAll] = useState([]); // State for all sale data
    const [loadingToday, setLoadingToday] = useState(true);
    const [loadingAll, setLoadingAll] = useState(true);
    const [loadingRoutine, setLoadingRoutine] = useState(false);
    const [loadingDelivery, setLoadingDelivery] = useState(false);
    const [showManageCard, setShowManageCard] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [manageStartDate, setManageStartDate] = useState(todayStr);
    const [manageEndDate, setManageEndDate] = useState(todayStr);
    const [errorToday, setErrorToday] = useState('');
    const [errorAll, setErrorAll] = useState('');
    const [startDate, setStartDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);
    const [agentReports, setAgentReports] = useState([]);
    const [loadingAgent, setLoadingAgent] = useState(false);
    const [agentCustomers, setAgentCustomers] = useState({});
    const navigate = useNavigate();

    // Helpers and computed summaries
    const isMultiDay = useMemo(() => startDate !== endDate, [startDate, endDate]);

    const numberOrZero = (v) => {
        const n = typeof v === 'string' ? parseFloat(v) : (typeof v === 'number' ? v : 0);
        return isNaN(n) ? 0 : n;
    };

    const saleSummary = useMemo(() => {
        const totals = { totalQty: 0, totalPrice: 0, shopCount: 0 };
        if (!Array.isArray(saleDataAll) || saleDataAll.length === 0) return totals;

        // Sum qty and price normally
        for (const row of saleDataAll) {
            totals.totalQty += numberOrZero(row.totalQty);
            totals.totalPrice += numberOrZero(row.totalPrice);
        }

        if (!isMultiDay) {
            // Single day: sum shopCount directly
            totals.shopCount = saleDataAll.reduce((acc, row) => acc + numberOrZero(row.shopCount), 0);
        } else {
            // Multi-day: average shopCount per user across their rows, then sum those averages
            const byUser = new Map();
            for (const row of saleDataAll) {
                const userKey = row.nameuser || row.username || row.user || 'unknown';
                if (!byUser.has(userKey)) byUser.set(userKey, { sum: 0, count: 0 });
                const entry = byUser.get(userKey);
                entry.sum += numberOrZero(row.shopCount);
                entry.count += 1;
            }
            let sumOfAverages = 0;
            byUser.forEach(({ sum, count }) => {
                if (count > 0) sumOfAverages += sum / count;
            });
            totals.shopCount = Math.round(sumOfAverages);
        }

        return totals;
    }, [saleDataAll, isMultiDay]);

    // Function to handle user logout
    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    // Function to get the token from local storage
    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            handleLogout();
            return null;
        }
        return JSON.parse(user)?.token;
    }, [navigate]);

    // Handlers for date selection (similar to dashboard)
    const handleEndDateChange = (e) => {
        const newEndDate = e.target.value;
        setEndDate(newEndDate);
        if (startDate > newEndDate) {
            setStartDate(newEndDate);
        }
    };

    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        if (newStartDate > endDate) {
            setEndDate(newStartDate);
        }
    };

    // Function to fetch all sale data
    const fetchSaleDataAll = useCallback(async () => {
        const token = checkToken();
        if (!token) return;
        setLoadingAll(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/by-date?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&isAgent=false`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 403) {
                    //handleLogout();
                    return;
                }
                const errorText = await response.text();
                setErrorAll(`Failed to fetch all sale data: ${errorText}`);
                throw new Error(`Failed to fetch all sale data: ${errorText}`);
            }

            const data = await response.json();
            console.log("API response:", data); // Debugging line
            setSaleDataAll(Array.isArray(data) ? data : [data]);
        } catch (error) {
            setErrorAll('Error fetching all sale data');
            console.error('Fetch error:', error);
        } finally {
            setLoadingAll(false);
        }
    }, [checkToken, handleLogout, startDate, endDate]);
    
    const fetchAgentReports = useCallback(async () => {
        const token = checkToken();
        if (!token) return;
        setLoadingAgent(true);
        try {
            const url = `${API_BASE_URL}/saledata/api/module/reports/by-date?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}&isAgent=true`;
            const resp = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (resp.ok) { const data = await resp.json(); setAgentReports(Array.isArray(data) ? data : []); }
        } catch (e) { console.error('Agent fetch error:', e); }
        finally { setLoadingAgent(false); }
    }, [checkToken, startDate, endDate]);

    const fetchAgentCustomers = useCallback(async () => {
        const token = checkToken();
        if (!token) return;
        try {
            const resp = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ type: 'AGENT' })
            });
            if (resp.ok) {
                const data = await resp.json();
                const list = Array.isArray(data) ? data : (Array.isArray(data?.agent) ? data.agent : (Array.isArray(data?.data) ? data.data : []));
                const map = {};
                list.forEach(c => {
                    const uid = c.agentCustomerUid || c.customerUid || c.uid;
                    const name = c.agentName || c.storeName || c.name || uid;
                    if (uid) map[uid] = name;
                });
                setAgentCustomers(map);
            }
        } catch (e) { console.error('Agent customer fetch error:', e); }
    }, [checkToken]);

    // Function to fetch Customer Buy Routine
    const fetchCustomerBuyRoutine = useCallback(async () => {
        const token = checkToken();
        if (!token) return;

        // Use the first seller from the list or we might need a seller selector.
        // However, the issue description says "Uses MCS_saledata + customerBuyData on seller on date"
        // and provides a sellerId in the example.
        // In SaleReportManagement, we have multiple sellers.
        // Usually, these routines are run for a specific seller or all.
        // Looking at the example body: { "sellerId": "U-5C787623", ... }
        // If there's no specific seller selected, we might need to ask or just use a default if applicable.
        // But SaleReportManagement shows a list of ALL sellers' reports for the date range.
        // Maybe it's better to add this button per row or a global one if it's meant for all?
        // Re-reading issue: "Fetch Customer Buy Rutine" ... "Body (example): sellerId: ..."
        // I will add a button in the row to trigger it for that specific seller.

        // Wait, if I add it to the row, I need to pass the sellerId.
    }, [checkToken]);

    const handleTriggerRoutine = async (sellerId, sDate, eDate) => {
        const token = checkToken();
        if (!token) return;

        setLoadingRoutine(true);
        try {
            const url = `${API_BASE_URL}/customer/api/customer-buy-rutine/fetch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    sellerId: sellerId,
                    startDate: sDate || manageStartDate,
                    endDate: eDate || manageEndDate
                }),
            });

            if (!response.ok) {
                const text = await response.text();
                alert(`Failed to fetch routine: ${text}`);
            } else {
                const data = await response.json();
                alert(`Routine updated successfully for ${sellerId}. Found ${data.length} customers.`);
            }
        } catch (error) {
            console.error('Routine fetch error:', error);
            alert('Error triggering routine');
        } finally {
            setLoadingRoutine(false);
        }
    };

    const handleCheckDelivery = async (sellerId, date) => {
        const token = checkToken();
        if (!token) return;

        setLoadingDelivery(true);
        try {
            const url = `${API_BASE_URL}/customer/api/customer-delivery-checker/check/user`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    sellerId: sellerId,
                    date: date || manageStartDate
                }),
            });

            if (!response.ok) {
                const text = await response.text();
                alert(`Failed to check delivery: ${text}`);
            } else {
                const data = await response.json();
                alert(`Delivery check completed for ${sellerId}. Found ${data.length} results.`);
            }
        } catch (error) {
            console.error('Delivery check error:', error);
            alert('Error checking delivery');
        } finally {
            setLoadingDelivery(false);
        }
    };

    const openManageCard = (sale) => {
        setSelectedReport(sale);
        setManageStartDate(sale.date);
        setManageEndDate(sale.date);
        setShowManageCard(true);
    };

    const closeManageCard = () => {
        setShowManageCard(false);
        setSelectedReport(null);
    };

    const goToDetail = (sale) => {
        window.open(
            `/saleDataDetail/${sale.reportCode || sale.id}`,
            "_blank"
        );
    };

    const goToAgentDetail = (sale) => {
        window.open(
            `/AgentSaleDataDetail/${encodeURIComponent(sale.reportCode || sale.id)}`,
            "_blank"
        );
    };

    useEffect(() => {
        fetchSaleDataAll();
    }, [ fetchSaleDataAll]);

    // Refetch all sale data when date range changes
    useEffect(() => {
        fetchSaleDataAll();
    }, [startDate, endDate, fetchSaleDataAll]);

    useEffect(() => {
        fetchAgentReports();
        if (Object.keys(agentCustomers).length === 0) fetchAgentCustomers();
    }, [startDate, endDate, fetchAgentReports]);

    return (
        <div className="btc-container">
            <h1>ตรวจสอบยอดขาย</h1>

            {/* Shared date range picker */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <label>ช่วงวันที่:</label>
                <input style={{ width: '12%' }} type="date" value={startDate} onChange={handleStartDateChange} />
                <span>ถึง</span>
                <input style={{ width: '12%' }} type="date" value={endDate} onChange={handleEndDateChange} />
                <button
                    className="btc-btn"
                    style={{ marginLeft: '16px', background: '#E60012', padding: '4px 14px', fontSize: '13px', fontWeight: 700 }}
                    onClick={() => navigate(`/accounting-daily-report?date=${startDate}`)}
                    title="รายงานบัญชีรายวันของวันเริ่มต้นที่เลือก"
                >
                    📊 รายงานบัญชีรายวัน
                </button>
                <button
                    className="btc-btn"
                    style={{ background: '#6B6B6B', padding: '4px 14px', fontSize: '13px', fontWeight: 700 }}
                    onClick={() => navigate(`/driver-daily-report?date=${startDate}`)}
                    title="รายงานสรุปย่อรายคนขับสำหรับวันเริ่มต้นที่เลือก"
                >
                    🚚 รายงานรายคนขับ
                </button>
                <button
                    className="btc-btn"
                    style={{ background: '#6B6B6B', padding: '4px 14px', fontSize: '13px', fontWeight: 700 }}
                    onClick={() => navigate(`/cargo-registration?date=${startDate}`)}
                    title="ลงทะเบียนขึ้น-กลับบรรจุ สำหรับวันเริ่มต้นที่เลือก"
                >
                    📦 ลงทะเบียนขึ้นกระสอบ
                </button>
            </div>

            {/* ===== คนขายปกติ (Delivery) — TOP ===== */}
            <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', marginBottom: '120px', overflow: 'hidden' }}>
                <div style={{ background: '#e9ecef', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', borderBottom: '2px solid #dee2e6' }}>
                    <h2 style={{ color: '#495057', margin: 0, fontSize: '14px', fontWeight: 700 }}>คนขายปกติ (Delivery)</h2>
                    <div style={{ display: 'flex', gap: '32px', color: '#6c757d', fontSize: '13px', flexWrap: 'wrap' }}>
                        <span>ขายรวม: <b style={{ color: '#343a40' }}>{saleSummary.totalQty.toLocaleString()}</b></span>
                        <span>รายได้: <b style={{ color: '#343a40' }}>{saleSummary.totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> ฿</span>
                        <span>ร้านค้า: <b style={{ color: '#343a40' }}>{saleSummary.shopCount.toLocaleString()}</b>{isMultiDay ? <em style={{ marginLeft: 6, color: '#adb5bd', fontSize: '12px' }}>(เฉลี่ย/สาย)</em> : null}</span>
                    </div>
                </div>
                <div style={{ padding: '16px 0' }}>
            {loadingAll ? (
                <p style={{ padding: '0 16px' }}>Loading all sale data...</p>
            ) : (
                <>
                    {errorAll && <p className="error" style={{ padding: '0 16px' }}>{errorAll}</p>}
                    <table className="btc-table">
                        <thead>
                        <tr>
                            <th>สาย</th>
                            <th>จำนวนการขายทั้งหมด</th>
                            <th>รายได้รวม</th>
                            <th>จำนวนร้านที่ส่งทั้งหมด</th>
                            <th>วันที่</th>
                            <th>เติมน้ำแข็ง</th>
                            <th>เวลาเริ่มส่ง</th>
                            <th>เวลาสุดท้ายที่ส่ง</th>
                            <th>จำนวนขายถุง</th>
                            <th>ยอดขายถุง</th>
                            <th>ส่งของ|ส่งรายงาน|ตรวจสอบ</th>
                            <th>จัดการ</th>
                            <th>ตัวเลือก</th>
                        </tr>
                        </thead>
                        <tbody>
                        {saleDataAll && saleDataAll.length > 0 ? (
                            saleDataAll.map((sale, index) => (
                                <tr key={index}>
                                    <td>{sale.nameuser? sale.nameuser:sale.username}</td>
                                    <td>{sale.totalQty}</td>
                                    <td>{sale.totalPrice}</td>
                                    <td>{sale.shopCount}</td>
                                    <td>{sale.date}</td>
                                    <td>{(sale.totalRefill ?? sale.totalMeltCompensation)}</td>
                                    <td>{sale.firstDelivery}</td>
                                    <td>{sale.lastDelivery}</td>
                                    <td>{sale.bagsaleTotalQty || 0}</td>
                                    <td>{sale.bagsaleTotalPrice || 0}</td>
                                    <td>
                                        <span style={{ color: sale.delivered ? "green" : "red" }}>█</span>|

                                        <span
                                            title={moneySubmittedState(sale) === null ? "ไม่ทราบสถานะ" : undefined}
                                            style={{ color: moneySubmittedState(sale) === null ? "gray" : isMoneySubmitted(sale) ? "green" : "red" }}
                                        >█</span>|

                                        <span style={{ color: sale.verified ? "green" : "red" }}>█</span>

                                    </td>
                                    <td>
                                        <button 
                                            className="btc-btn btn-sm"
                                            onClick={() => openManageCard(sale)}
                                            style={{ backgroundColor: "#17a2b8", fontSize: "11px", padding: "2px 5px" }}
                                        >
                                            จัดการ
                                        </button>
                                    </td>
                                    <td>
                                        <i
                                            className="fa-solid fa-eye"
                                            style={{ color: "gray", cursor: "pointer" }}
                                            onClick={() => goToDetail(sale)}
                                        ></i>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="13">ไม่มีข้อมูลการขายในระบบ</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </>
            )}
                </div>
            </div>

            {/* ===== Agent (ตัวแทน) — BOTTOM ===== */}
            <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', overflow: 'hidden' }}>
                <div style={{ background: '#e9ecef', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', borderBottom: '2px solid #dee2e6' }}>
                    <h2 style={{ color: '#495057', margin: 0, fontSize: '14px', fontWeight: 700 }}>Agent (ตัวแทน)</h2>
                    <div style={{ display: 'flex', gap: '32px', color: '#6c757d', fontSize: '13px', flexWrap: 'wrap' }}>
                        <span>ขายรวม: <b style={{ color: '#343a40' }}>{agentReports.reduce((s, r) => s + (r.totalQty || 0), 0).toLocaleString()}</b></span>
                        <span>รายได้: <b style={{ color: '#343a40' }}>{agentReports.reduce((s, r) => s + (r.totalPrice || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b> ฿</span>
                        <span>ร้านค้า: <b style={{ color: '#343a40' }}>{agentReports.reduce((s, r) => s + (r.shopCount || 0), 0).toLocaleString()}</b></span>
                    </div>
                </div>
                <div style={{ padding: '16px 0' }}>
            {loadingAgent ? (
                <p style={{ padding: '0 16px' }}>กำลังโหลด Agent data...</p>
            ) : (
                <table className="btc-table">
                    <thead>
                    <tr>
                        <th>ตัวแทน (Agent)</th>
                        <th>วันที่</th>
                        <th>จำนวนร้านที่ส่ง</th>
                        <th>จำนวนสินค้า</th>
                        <th>รายได้รวม</th>
                        <th>ตรวจสอบ</th>
                        <th>ตัวเลือก</th>
                    </tr>
                    </thead>
                    <tbody>
                    {agentReports.length > 0 ? agentReports.map((r, i) => (
                        <tr key={i}>
                            <td>{agentCustomers[r.sellerId] || r.nameuser || r.sellerId}</td>
                            <td>{r.date}</td>
                            <td>{r.shopCount}</td>
                            <td>{r.totalQty}</td>
                            <td>{r.totalPrice != null ? r.totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0}</td>
                            <td><span style={{ color: r.verified ? 'green' : 'red' }}>█</span></td>
                            <td>
                                {r.reportCode && (
                                    <i className="fa-solid fa-eye" style={{ color: 'gray', cursor: 'pointer' }} onClick={() => goToAgentDetail(r)} />
                                )}
                            </td>
                        </tr>
                    )) : (
                        <tr><td colSpan="7">ไม่มีข้อมูล Agent ในช่วงวันที่นี้</td></tr>
                    )}
                    </tbody>
                </table>
            )}
                </div>
            </div>

            {/* Manage Actions Card (Modal) */}
            {showManageCard && selectedReport && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div className="btc-card" style={{ width: '400px', padding: '20px', position: 'relative' }}>
                        <button 
                            onClick={closeManageCard}
                            style={{ position: 'absolute', top: '10px', right: '10px', border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer' }}
                        >
                            &times;
                        </button>
                        <h3>จัดการรายงาน: {selectedReport.nameuser || selectedReport.username}</h3>
                        <p>วันที่ในรายงาน: {selectedReport.date}</p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ width: '60px' }}>เริ่ม:</label>
                                <input 
                                    type="date" 
                                    value={manageStartDate} 
                                    onChange={(e) => setManageStartDate(e.target.value)}
                                    style={{ flex: 1, padding: '5px' }}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <label style={{ width: '60px' }}>ถึง:</label>
                                <input 
                                    type="date" 
                                    value={manageEndDate} 
                                    onChange={(e) => setManageEndDate(e.target.value)}
                                    style={{ flex: 1, padding: '5px' }}
                                />
                            </div>
                            
                            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                                <button 
                                    className="btc-btn"
                                    style={{ flex: 1, backgroundColor: "#46af38" }}
                                    onClick={() => handleCheckDelivery(selectedReport.sellerId || selectedReport.uid || selectedReport.user, manageStartDate)}
                                    disabled={loadingDelivery}
                                >
                                    {loadingDelivery ? '...' : 'Check Delivery'}
                                </button>
                                <button 
                                    className="btc-btn"
                                    style={{ flex: 1, backgroundColor: "#007bff" }}
                                    onClick={() => handleTriggerRoutine(selectedReport.sellerId || selectedReport.uid || selectedReport.user, manageStartDate, manageEndDate)}
                                    disabled={loadingRoutine}
                                >
                                    {loadingRoutine ? '...' : 'Fetch Routine'}
                                </button>
                            </div>
                            
                            <hr style={{ margin: '15px 0' }} />
                            
                            <button 
                                className="btc-btn"
                                style={{ backgroundColor: "#6c757d" }}
                                onClick={() => {
                                    const report = selectedReport;
                                    closeManageCard();
                                    goToDetail(report);
                                }}
                            >
                                ดูรายละเอียดรายงานแบบเต็ม
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SaleReportManagement;
